import React, { useEffect, useState, useCallback } from 'react';

interface Move {
  fromSpan: [number, number];
  toSpan: [number, number];
  editIndex: number;
}

interface BezierOverlayProps {
  moves: Move[];
  containerRef: React.RefObject<HTMLDivElement>;
}

interface CurveData {
  path: string;
  color: string;
}

const BezierOverlay: React.FC<BezierOverlayProps> = ({ moves, containerRef }) => {
  const [curves, setCurves] = useState<CurveData[]>([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const calculateCurves = useCallback(() => {
    if (!containerRef.current || moves.length === 0) {
      setCurves([]);
      return;
    }

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    setDimensions({
      width: containerRect.width,
      height: containerRect.height,
    });

    const newCurves: CurveData[] = [];

    moves.forEach((move, index) => {
      // Find the source element (where the word is now)
      const fromElement = container.querySelector(
        `[data-edit-index="${move.editIndex}"][data-move-type="source"]`
      ) as HTMLElement;

      // Find the destination element (triangle marker where word should go)
      const toElement = container.querySelector(
        `[data-edit-index="${move.editIndex}"][data-move-type="destination"]`
      ) as HTMLElement;

      if (!fromElement || !toElement) return;

      const fromRect = fromElement.getBoundingClientRect();
      const toRect = toElement.getBoundingClientRect();

      // Calculate source position (center-top of the source word)
      const fromX = fromRect.left - containerRect.left + fromRect.width / 2;
      const fromY = fromRect.top - containerRect.top;

      // Calculate destination position (center of the destination marker)
      const toX = toRect.left - containerRect.left + toRect.width / 2;
      const toY = toRect.top - containerRect.top + toRect.height / 2;

      // Calculate control point for quadratic Bezier curve
      const midX = (fromX + toX) / 2;
      const horizontalDistance = Math.abs(toX - fromX);
      const deltaH = Math.max(horizontalDistance * 0.3, 25); // Curve height
      const controlY = Math.min(fromY, toY) - deltaH;

      // Create SVG path - from source to destination
      const path = `M ${fromX} ${fromY} Q ${midX} ${controlY}, ${toX} ${toY}`;

      // Generate a color for each move
      const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'];
      const color = colors[index % colors.length];

      newCurves.push({ path, color });
    });

    setCurves(newCurves);
  }, [moves, containerRef]);

  useEffect(() => {
    calculateCurves();

    // Recalculate on resize
    const handleResize = () => {
      requestAnimationFrame(calculateCurves);
    };

    window.addEventListener('resize', handleResize);

    // Also recalculate when container content might change
    const observer = new MutationObserver(calculateCurves);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [calculateCurves, containerRef]);

  if (curves.length === 0) return null;

  return (
    <svg
      className="bezier-overlay"
      width={dimensions.width}
      height={dimensions.height}
      style={{ position: 'absolute', top: 0, left: 0 }}
    >
      <defs>
        {/* Arrow markers for each curve */}
        {curves.map((curve, index) => (
          <marker
            key={`arrow-${index}`}
            id={`arrowhead-${index}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={curve.color} />
          </marker>
        ))}
      </defs>

      {/* Render curves */}
      {curves.map((curve, index) => (
        <g key={index}>
          {/* Shadow for depth */}
          <path
            d={curve.path}
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ transform: 'translate(2px, 2px)' }}
          />
          {/* Main curve */}
          <path
            d={curve.path}
            className="bezier-curve"
            stroke={curve.color}
            strokeWidth="2"
            strokeLinecap="round"
            markerEnd={`url(#arrowhead-${index})`}
          />
        </g>
      ))}
    </svg>
  );
};

export default BezierOverlay;
